import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useHistory } from "react-router";
import { trackEvent } from "@hopper-b2b/api";
import {
  AirDisruptionSelectors,
  ContactSelectors,
  DesktopCheckoutHeader,
  FlightSelectors,
  SeatSelectors,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { cartFulfillSelectors as checkoutCartFulfillSelectors } from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import {
  BOOKING_CONFIRMATION,
  type FlightSummaryInfo,
  TripCategory,
} from "@hopper-b2b/types";
import { FlightConfirmation, MobileFloatingSection } from "@hopper-b2b/ui";
import {
  useDeviceTypes,
  useEnableDarkMode,
  useUberBridge,
} from "@hopper-b2b/utilities";
import { Box } from "@material-ui/core";
import clsx from "clsx";

import AirplaneArrive from "../../../../assets/client/airplane-arrive.svg";
import AirplaneDepart from "../../../../assets/client/airplane-depart.svg";
import AirplaneArriveWhite from "../../../../assets/client/darkMode/airplane-arrive-white.svg";
import AirplaneDepartWhite from "../../../../assets/client/darkMode/airplane-depart-white.svg";
import config from "../../../../utils/config";
import { PATH_HOME } from "../../../../utils/urlPaths";
import {
  getClientAssets,
  getSelectedTripDetails,
  getTripCategory,
} from "../../selectors";
import { cartFulfillSelectors } from "../../states/CartFulfill";
import "./styles.scss";

export const BookingConfirmationWrapper: FC<
  PropsWithChildren<{ matchesMobile: boolean }>
> = ({ matchesMobile, children }) => {
  const clientAssets = useSelector(getClientAssets);
  const { t } = useI18nContext();

  return (
    <Box className="booking-confirmation-container uber">
      {matchesMobile ? (
        children
      ) : (
        <Box className="booking-confirmation-desktop-container">
          <DesktopCheckoutHeader
            clientLogo={clientAssets?.logo}
            title={t("bookingSuccess.title")}
            subtitle={t("bookingSuccess.subtitle")}
          />
          <Box className="booking-confirmation-content-container">
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const BookingConfirmation = () => {
  const { setHeader } = useUberBridge();
  const history = useHistory();
  const { t } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();
  const enabledDarkMode = useEnableDarkMode();
  const isDarkMode = enabledDarkMode && getDarkModePreferred();

  const contactInformation = useSelector(
    ContactSelectors.getContactInformation
  );
  const itinerary = useSelector(FlightSelectors.getFinalizedItinerary);
  const seats = useSelector(SeatSelectors.getFulfilledSeats);
  const isRoundTrip = useSelector(getTripCategory) === TripCategory.ROUND_TRIP;
  const tripDetails = useSelector(getSelectedTripDetails);
  const airportMap = useSelector(FlightSelectors.getAirportMap);
  const hasPurchasedAirCfar = useSelector(
    cartFulfillSelectors.hasFulfilledAirCfarProduct
  );
  const hasPurchasedDisruption = useSelector(
    cartFulfillSelectors.hasFulfilledDisruptionProduct
  );
  const hasPurchasedAirChfar = useSelector(
    cartFulfillSelectors.hasFulfilledAirChfarProduct
  );
  const purchasedDisruptionProducts = useSelector(
    AirDisruptionSelectors.getPurchasedDisruptionProducts
  );

  const getFlightSummaryInfo = useCallback(
    (isOutgoing: boolean): FlightSummaryInfo | null => {
      const slice = tripDetails.slices.find((s) => s.outgoing === isOutgoing);
      if (slice) {
        return {
          originCity: slice.originName,
          destinationCity: slice.destinationName,
          arrival: slice.arrivalTime,
          departure: slice.departureTime,
          airlineName: slice.segmentDetails[0].airlineName,
          airlineCode: slice.segmentDetails[0].airlineCode,
          isDeparture: slice.outgoing,
          title: t("originToDestination", {
            origin: airportMap[slice.originCode].servedCityName,
            destination: airportMap[slice.destinationCode].servedCityName,
          }),
          stops: slice.stops,
        };
      }
      return null;
    },
    [t, tripDetails.slices]
  );

  useEffect(() => {
    document.title = t("bookingConfirmation.documentTitle");
    return () => {
      document.title = t("documentTitle");
    };
  }, [t]);

  const bookingSubtitle = useMemo(
    () =>
      t("bookingConfirmation.subtitle", {
        email: contactInformation.email,
      }),
    [contactInformation.email, t]
  );

  const userAction = useSelector(
    checkoutCartFulfillSelectors.getCartFulfillUserActions
  );

  useEffect(() => {
    trackEvent({ eventName: BOOKING_CONFIRMATION, properties: undefined });
  }, []);

  useEffect(() => {
    setHeader({
      title: t("bookingConfirmation.documentTitle"),
    });
  }, [setHeader, t]);

  useEffect(() => {
    if (!userAction?.url) return;
    console.log("userAction URL:", userAction.url);
    const message = {
      type: "CONFIRM_RYANAIR",
      payload: {
        ryanairUrl: userAction.url,
      },
    };

    window.parent.postMessage(message, config.UBER_URL);
  }, [userAction?.url]);

  if (userAction?.url) {
    if (config.ENV !== "production") {
      return (
        // eslint-disable-next-line i18next/no-literal-string
        <a href={userAction.url} target="_blank" rel="noopener noreferrer">
          (DEV ONLY) Go to Ryanair Confirmation
        </a>
      );
    }
    return null;
  }

  return (
    <BookingConfirmationWrapper matchesMobile={matchesMobile}>
      <Box
        className={clsx("uber-flight-booking-success-container", {
          mobile: matchesMobile,
        })}
      >
        <FlightConfirmation
          statusLabel={t("confirmed")}
          title={t("bookingConfirmation.title")}
          subtitle={bookingSubtitle}
          nextHeader={t("bookingConfirmation.whatsNext")}
          outgoingFlightSummaryInfo={getFlightSummaryInfo(true)}
          returningFlightSummaryInfo={
            isRoundTrip ? getFlightSummaryInfo(false) : undefined
          }
          isMobile={matchesMobile}
          seats={seats?.seats}
          assets={{
            airplaneDepart: isDarkMode ? AirplaneDepartWhite : AirplaneDepart,
            airplaneArrive: isDarkMode ? AirplaneArriveWhite : AirplaneArrive,
          }}
          hasPurchasedAirCfar={hasPurchasedAirCfar}
          hasPurchasedDisruption={hasPurchasedDisruption}
          hasPurchasedAirChfar={hasPurchasedAirChfar}
          purchasedDisruptionProducts={purchasedDisruptionProducts}
        />
        <MobileFloatingSection
          className="return-home-button"
          primaryButton={{
            children: t("viewTrip"),
            onClick: () => {
              const agentLocator = itinerary?.locators?.agent.unscopedValue;
              if (agentLocator) {
                window.parent.postMessage(
                  {
                    type: "BOOKING_ENDED",
                    payload: { agentLocator },
                  },
                  config.UBER_URL
                );
              } else {
                history.push(PATH_HOME);
              }
            },
          }}
        />
      </Box>
    </BookingConfirmationWrapper>
  );
};
